/**
 * Device-scoped memory of which provider was used here.
 *
 * All access is defensive: Safari in private mode throws on `localStorage`
 * access rather than returning null, and Firefox can too when storage is
 * blocked. A remembered-provider hint is a nicety — it must never be the reason
 * somebody cannot sign in.
 */
import { isKnownProvider, type ProviderId, shouldPromote } from "../../shared/last-provider";

const LAST_KEY = "rcm.last-provider";
const PENDING_KEY = "rcm.pending-provider";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked or full. The hint simply never appears.
  }
}

function remove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* see write() */
  }
}

/** The provider this device signed in with last, if any. */
export function lastProvider(): ProviderId | null {
  const value = read(LAST_KEY);
  return isKnownProvider(value) ? value : null;
}

/**
 * Record an attempt. Not yet the remembered provider — a redirect that the user
 * cancels must not teach us the wrong answer.
 */
export function rememberAttempt(provider: ProviderId) {
  write(PENDING_KEY, provider);
}

/**
 * Promote a pending attempt once a real session exists.
 *
 * Called from session.refresh() rather than from the resume path, because a
 * confirmed session is the only trustworthy evidence that the round trip
 * actually succeeded — a provider can redirect back to us on denial too.
 */
export function promoteIfSignedIn(signedIn: boolean) {
  const pending = read(PENDING_KEY);
  if (shouldPromote(pending, signedIn)) {
    write(LAST_KEY, pending);
    remove(PENDING_KEY);
  } else if (!signedIn && pending) {
    // An attempt that did not produce a session. Drop it rather than leaving it
    // to be promoted by some unrelated later sign-in.
    remove(PENDING_KEY);
  }
}

/** Sign-out deliberately does NOT clear this — remembering is the whole point. */
export function forgetProvider() {
  remove(LAST_KEY);
  remove(PENDING_KEY);
}
